import { Toaster, toast } from "sonner";
import React, { Fragment, useState, useRef, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import axiosCacheAdapter from "axios-cache-adapter";
import "react-loading-skeleton/dist/skeleton.css";
import { useFormik } from "formik";


const cache = axiosCacheAdapter.setupCache({
    maxAge: 5 * 60 * 1000,
});

const api = axios.create({
    adapter: cache.adapter,
});

var userEmail;
var JSToken;
var accessToken;
if (localStorage.user) {
    userEmail = localStorage.user_email;
    JSToken = JSON.parse(localStorage.user);
    accessToken = JSToken.access_token;
}

const config = {
    headers: { Authorization: `Bearer ${accessToken}` },
};


const STATUS_COLORS = {
    "Total Amount": "bg-blue-500",
    "Total Principal Amount": "bg-yellow-500",
    "Commission": "bg-green-500",
};

const CrPayment = () => {
    const [loadingSkeleton, setLoadingSkeleton] = useState(true);
    const [onFilters, setOnFilters] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    const [allPayments, setAllPayments] = useState(null);
    const [totalRegEntries, setTotalRegEntries] = useState();
    const [apiFilters, setApiFilters] = useState(
        '["status","in",["payment_captured","payment_approved"]],' +
        '["purchase_type","in",["Registration First Installment","Registration Second Installment","Registration Full"]],' +
        '["payment_environment","=","Production"],' +
        '["registration_request","!=",""]'
    );
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState();

    const tableRef = useRef(null);

    const applyFilters = useFormik({
        initialValues: {
            start_date: "",
            end_date: ""
        },
        onSubmit: (values) => {
            setOnFilters(false);
            setLoadingSkeleton(true)
            if (values.start_date && values.end_date && values.start_date > values.end_date) {
                toast.error('Invalid Date Range')
                setLoadingSkeleton(false)
                return;
            }
            else if (values.start_date && values.end_date && values.start_date < values.end_date) {
                if (values.start_date.length >= 1 && values.end_date.length >= 1) {
                    setApiFilters((prevState) => [
                        prevState +
                        `,["requested_on",">=","${values.start_date} 00:00:00"],["requested_on","<=","${values.end_date} 23:59:59"]`,
                    ]);

                }
            }
            else if (values.start_date) {
                if (values.start_date.length >= 1) {
                    setApiFilters((prevState) => [
                        prevState +
                        `,["requested_on",">=","${values.start_date}"]`,
                    ]);
                }
            }
            else if (values.end_date) {
                if (values.end_date.length >= 1) {
                    setApiFilters((prevState) => [
                        prevState +
                        `,["requested_on","<=","${values.end_date}"]`,
                    ]);
                }
            }
        }
    });

    useEffect(() => {
        const fetchallPayments = async () => {
            try {
                setLoading(true);
                setIsLoading(true);
                await api.get(
                    `https://dash.bestdiplomats.org/api/method/cr-payment-commission?filters=[${apiFilters}]`,
                    config
                )
                    .then((response) => {
                        setTotalRegEntries(response.data.data.total_count);
                        setAllPayments(response.data.data.payments);
                        setData(response.data.data.summary)
                        setLoadingSkeleton(false);
                        setIsNotFound(false);
                        setLoading(false);
                    });
                setIsLoading(false);
            } catch (e) {
                console.log(e);
                if (e?.code == "ERR_NETWORK" || e?.response?.status == 404) {
                    setTotalRegEntries(0);
                    setAllPayments([]);
                    toast.error('No Data Found')
                    setIsNotFound(true)
                    setLoadingSkeleton(true)
                } else {
                    setLoadingSkeleton(false)
                    toast.error('No Data Found')
                    setIsNotFound(true)
                    setTotalRegEntries(0);
                }
            }
        };
        fetchallPayments();
    }, [apiFilters]);

    return (
        <div>
            <div className="bg-white rounded-xl shadow-md p-6 w-full max-w-5xl mx-auto">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">
                    Total Payment Status
                </h2>

                {loading ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            {data?.map(({ label, value }) => (
                                <div
                                    key={label}
                                    className={`p-4 rounded-lg text-white shadow-md ${STATUS_COLORS[label] || "bg-gray-400"
                                        }`}
                                >
                                    <p className="text-sm font-medium">{label}</p>
                                    <p className="text-2xl font-bold">${value?.toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {     /* Header */}
            <div className="flex justify-end mt-3 gap-2">
                <Toaster position="bottom-left" richColors />
                {loadingSkeleton ? (
                    <>
                        <Skeleton width={80} height={40} />{" "}
                        <Skeleton width={80} height={40} />{" "}
                        <Skeleton width={80} height={40} />{" "}
                        <Skeleton width={80} height={40} />
                    </>
                ) : (
                    <>
                        {/* Search Bar */}
                        {/* Header Actions: Search (left) + Filters (right) */}
                        <div className="flex flex-col sm:flex-row justify-end items-center mt-3 gap-3 w-full">
                            {/* 🛠 Filters (always right) */}
                            <div className="flex items-center gap-2 justify-end w-full sm:w-auto">
                                {/* Filter */}
                                <button
                                    onClick={() => setOnFilters(true)}
                                    className="inline-flex justify-center gap-1 rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    <span>Filter</span>
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                        ></path>
                                    </svg>
                                </button>

                                {/* Clear Filter */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        applyFilters.resetForm();
                                        setApiFilters(
                                            '["status","in",["payment_captured","payment_approved"]],' +
                                            '["purchase_type","in",["Registration First Installment","Registration Second Installment","Registration Full"]],' +
                                            '["payment_environment","=","Production"],' +
                                            '["registration_request","!=",""]'
                                        );
                                    }}
                                    className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Clear Filter
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {    /* Drawer for Filters */}
            <Transition.Root show={onFilters} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setOnFilters}>
                    <div className="fixed inset-0" />

                    <div className="fixed inset-0 overflow-hidden">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                                <Transition.Child
                                    as={Fragment}
                                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                                        <form
                                            className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl"
                                            onSubmit={applyFilters.handleSubmit}
                                        >
                                            <div className="flex-1">
                                                {/* Header */}
                                                <div className="bg-gray-100 px-4 py-6 sm:px-6">
                                                    <div className="flex items-start justify-between space-x-3">
                                                        <div className="space-y-1">
                                                            <Dialog.Title className="text-lg flex gap-2 font-medium text-gray-900">
                                                                <span>Filters</span>{" "}
                                                                <svg
                                                                    className="w-6 h-6 mt-1"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                                                    ></path>
                                                                </svg>
                                                            </Dialog.Title>
                                                        </div>
                                                        <div className="flex h-7 items-center">
                                                            <button
                                                                type="button"
                                                                className="text-gray-400 hover:text-gray-500"
                                                                onClick={() => {
                                                                    setOnFilters(false);
                                                                }}
                                                            >
                                                                <span className="sr-only">Close panel</span>
                                                                <XMarkIcon
                                                                    className="h-6 w-6"
                                                                    aria-hidden="true"
                                                                />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Divider container */}
                                                <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                                                    <h4 className="text-lg font-semibold text-gray-800 pb-2 mb-4 mt-5 px-4">
                                                        Country Representative Administrator
                                                    </h4>
                                                    <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                                                        <label
                                                            htmlFor="start_date"
                                                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                                                        >
                                                            Start Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            name="start_date"
                                                            id="start_date"
                                                            onChange={applyFilters.handleChange}
                                                            value={applyFilters.values.start_date}
                                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                        />
                                                    </div>
                                                    <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                                                        <label
                                                            htmlFor="end_date"
                                                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                                                        >
                                                            End Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            name="end_date"
                                                            id="end_date"
                                                            onChange={applyFilters.handleChange}
                                                            value={applyFilters.values.end_date}
                                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                        />
                                                    </div>
                                                    <h4 className="text-lg font-semibold text-gray-800 pb-2 mb-4 mt-5 px-4 mb-3 pt-3">
                                                        Country Representative
                                                    </h4>
                                                    <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                                                        <label
                                                            htmlFor="cr_end_date"
                                                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                                                        >
                                                            End Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            name="cr_end_date"
                                                            id="cr_end_date"
                                                            onChange={applyFilters.handleChange}
                                                            value={applyFilters.values.cr_end_date}
                                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                        />
                                                    </div>
                                                    <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                                                        <label
                                                            htmlFor="end_date"
                                                            className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                                                        >
                                                            End Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            name="end_date"
                                                            id="end_date"
                                                            onChange={applyFilters.handleChange}
                                                            value={applyFilters.values.cr_end_date}
                                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action buttons */}
                                            <div className="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                                                <div className="flex justify-end space-x-3">
                                                    <button
                                                        type="submit"
                                                        className="inline-flex color justify-center rounded-md border border-transparent bg-blue-500 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                    >
                                                        Apply Filter
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            applyFilters.resetForm();
                                                            setLoadingSkeleton(true)
                                                            setApiFilters('["status","in",["payment_captured","payment_approved"]],' +
                                                                '["purchase_type","in",["Registration First Installment","Registration Second Installment","Registration Full"]],' +
                                                                '["payment_environment","=","Production"],' +
                                                                '["registration_request","!=",""]');
                                                        }}
                                                        className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                    >
                                                        Clear Filter
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                        onClick={() => setOnFilters(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {    /* Total Registrations Header */}
            <div className="mt-1 py-1 divide-y ">
                <p className="felx justify-end text-right font-semibold text-gray-600">
                    {loadingSkeleton ? (
                        <Skeleton width={40} height={20} />
                    ) : (
                        <>Total Payments: {allPayments?.length} </>
                    )}
                </p>
            </div>

            { /* Main Table */}
            {isNotFound ? <div className="h-screen flex justify-center flex-col items-center ">
                <svg fill="none" className="w-40 h-40 text-gray-400" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                </svg> <span className=" text-lg text-gray-400">  No Data Found</span>
            </div> : <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <div className="max-h-[500px] overflow-y-auto"> {/* Fixed height & scroll */}
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-stone-100 sticky top-0 z-10">
                            <tr>
                                <th
                                    scope="col"
                                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-800 sm:pl-6"
                                >
                                    User
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                                >
                                    Request Date
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                                >
                                    Amount
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                                >
                                    Purchase Type
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                                >
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {allPayments?.map((payment) => (
                                <tr key={payment.name} className="cursor-pointer hover:bg-gray-50">
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        <div className="text-gray-900">{payment.user}</div>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        <div className="text-gray-900">
                                            {payment.requested_on?.split(" ")[0]}
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        <div className="text-gray-900">${payment.amount / 100}</div>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        <div className="text-gray-900">{payment.purchase_type}</div>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        {payment.status == "payment_captured" ? (
                                            <span
                                                className="inline-flex rounded-full px-2 text-xs font-semibold leading-5"
                                                style={{
                                                    backgroundColor: "#d1fae5",
                                                    color: "#065f46",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    padding: "0.25rem 0rem",
                                                }}
                                            >
                                                {payment.status}
                                            </span>
                                        ) : payment.status == "payment_approved" ? (
                                            <span
                                                className="inline-flex rounded-full px-2 text-xs font-semibold leading-5"
                                                style={{
                                                    backgroundColor: "#fef3c7",
                                                    color: "#92400e",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    padding: "0.25rem 0rem",
                                                }}
                                            >
                                                {payment.status}
                                            </span>
                                        ) : (
                                            <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5">
                                                {payment.status}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            }
        </div >

    )
}

export default CrPayment;


